import { FC, useMemo, useState } from 'react';

import { isEqual } from 'lodash';
import useFilters from 'utils/dashboard/useFilters';
import { calculateFiltersCount } from 'utils/dashboard/utils';

import dashboardFiltersFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_filters_form_submit';

import Button, { ButtonKind, ButtonScale } from 'bloko/blocks/button';
import HSpacing from 'bloko/blocks/hSpacing';
import VSpacing from 'bloko/blocks/vSpacing';

import useTrls from 'hooks/translation/useTrls';

import { fetchCampaignsCount } from 'components/CampaignCard/CampaignInfo/Actions/fetchCampaignsCount';
import { fetchCampaignsNextPage } from 'components/Dashboard/CampaignList/fetchCampaignsNextPage';
import Filter from 'components/Dashboard/Filters/Filter/Filter';

import { setPage } from 'store/autoReducers';
import {
    AppliedFilters,
    CampaignLoadState,
    FilterName,
    INITIAL_APPLIED_FILTERS_STATE,
    setAppliedFilters,
    setAppliedFiltersCount,
    setLoadCampaigns,
} from 'store/models/dashboard';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/Dashboard/Filters/FiltersDropContent/FiltersDropContent.module.less';

const TrlKeys = {
    buttons: {
        reset: 'Dashboard.Filters.FiltersDrop.button.reset',
        apply: 'Dashboard.Filters.FiltersDrop.button.apply',
    },
    labels: {
        productTypes: 'Dashboard.Filters.Filter.ProductType.label',
        campaignStatuses: 'Dashboard.Filters.Filter.CampaignState.label',
        budgetStatuses: 'Dashboard.Filters.Filter.BudgetState.label',
        moderationStatuses: 'Dashboard.Filters.Filter.ModerationState.label',
        areas: 'Dashboard.Filters.Filter.areas.label',
        professionalRoles: 'Dashboard.Filters.Filter.professionalRoles.label',
        createdByUserIds: 'Dashboard.Filters.Filter.createdByUserIds.label',
    },
};

interface FiltersDropContentProps {
    closeDrop?: () => void;
}

const FiltersDropContent: FC<FiltersDropContentProps> = ({ closeDrop }) => {
    const appliedFilters = useSelector((state) => state.dashboard.appliedFilters);
    const currentStatus = useSelector((state) => state.campaignTabsStatus);
    const [selectedFilters, setSelectedFilters] = useState<AppliedFilters>({ ...appliedFilters });
    const dispatch = useDispatch();
    const filters = useFilters();

    const trl = useTrls();

    const onChangeFilterValue = (filterName: FilterName, id: string) => {
        setSelectedFilters((state) => {
            const filter = state[filterName] || [];

            const foundIndex = filter.indexOf(id);
            const newFiltersByName = [...filter];

            if (foundIndex >= 0) {
                newFiltersByName.splice(foundIndex, 1);
            } else {
                newFiltersByName.push(id);
            }

            return {
                ...selectedFilters,
                [filterName]: newFiltersByName.sort(),
            };
        });
    };

    const onApplyFilters = () => {
        closeDrop?.();
        dispatch([
            setLoadCampaigns(CampaignLoadState.Success),
            setPage(0),
            setAppliedFilters({ ...selectedFilters }),
            setAppliedFiltersCount(calculateFiltersCount(selectedFilters)),
        ]);

        dashboardFiltersFormSubmit({
            productType: selectedFilters.productTypes?.join(','),
            campaignState: selectedFilters.campaignStatuses?.join(','),
            budgetState: selectedFilters.budgetStatuses?.join(','),
            moderationState: selectedFilters.moderationStatuses?.join(','),
            areaId: selectedFilters.areas?.join(','),
            professionalRoles: selectedFilters.professionalRoles?.join(','),
        });

        void dispatch(fetchCampaignsNextPage({ currentStatus, isFilters: true }));
        void dispatch(fetchCampaignsCount());
    };

    const onReset = () => {
        closeDrop?.();
        dispatch([
            setLoadCampaigns(CampaignLoadState.Success),
            setPage(0),
            setAppliedFilters({ ...INITIAL_APPLIED_FILTERS_STATE }),
            setAppliedFiltersCount(0),
        ]);
        void dispatch(fetchCampaignsNextPage({ currentStatus, isFilters: true }));
        void dispatch(fetchCampaignsCount());
    };

    const showApplyButton = !isEqual(selectedFilters, appliedFilters);
    const showResetButton = useMemo(() => {
        const filterCount = calculateFiltersCount(selectedFilters);
        return filterCount > 0;
    }, [selectedFilters]);

    return (
        <div className={styles.dropContent}>
            <div className={styles.filtersWrapper}>
                {Object.entries(filters).map(([filterName, filter]) => {
                    return (
                        <Filter
                            key={filterName}
                            filterName={filterName as FilterName}
                            legend={trl(TrlKeys.labels[filterName as FilterName])}
                            filterList={filter}
                            checkedValues={selectedFilters[filterName as FilterName] || []}
                            onChange={onChangeFilterValue}
                        />
                    );
                })}
            </div>

            {(showResetButton || showApplyButton) && <VSpacing base={5} />}
            <div className={styles.buttons}>
                {showResetButton && (
                    <>
                        <Button onClick={onReset} scale={ButtonScale.Small} stretched data-qa="filters-reset">
                            {trl(TrlKeys.buttons.reset)}
                        </Button>
                        <HSpacing base={2} />
                    </>
                )}
                {showApplyButton && (
                    <Button
                        kind={ButtonKind.Primary}
                        onClick={onApplyFilters}
                        scale={ButtonScale.Small}
                        stretched
                        data-qa="filters-apply"
                    >
                        {trl(TrlKeys.buttons.apply)}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default FiltersDropContent;
